import React, { useRef, useEffect, useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Avatar,
  CircularProgress,
  Chip
} from '@mui/material';
import {
  Schedule,
  QueueMusic,
  Headphones,
  Radio
} from '@mui/icons-material';
import SectionTitle from '../common/SectionTitle';
import { fixImageUrl } from '../../utils/imageUtils';

const TodayScheduleSection = ({
  todaySchedule,
  scheduleLoading,
  isProgramLive,
  formatTime,
  liveProgram,
}) => {
  const scrollContainerRef = useRef(null);
  const nextProgramRef = useRef(null);
  const prevNextProgramIdRef = useRef(null);
  const [currentTimeTick, setCurrentTimeTick] = useState(Date.now());

  // Intervalo a cada 30 segundos para manter os horários e programas sincronizados com o tempo real
  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentTimeTick(Date.now());
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  const displayTime = (time) => {
    if (!time) return '';
    if (typeof formatTime === 'function') {
      try {
        return formatTime(time);
      } catch (e) {
        return time.slice(0, 5);
      }
    }
    return time.slice(0, 5);
  };

  // Build sorted programs list
  const allPrograms = [];

  if (todaySchedule?.programs && todaySchedule.programs.length > 0) {
    todaySchedule.programs.forEach((program) => {
      const isAbsent = Boolean(program.is_dj_absent || program.isDjAbsent);
      allPrograms.push({
        ...program,
        type: isAbsent ? 'default' : 'dj',
        isLive: isProgramLive ? isProgramLive(program) : false
      });
    });
  }

  if (todaySchedule?.defaultPrograms && todaySchedule.defaultPrograms.length > 0) {
    todaySchedule.defaultPrograms.forEach((program) => {
      allPrograms.push({
        ...program,
        type: 'default',
        isLive: isProgramLive ? isProgramLive(program) : false,
        dj: program.dj || { name: 'AutoDJ NostalBeat', city: 'Estúdio Central' }
      });
    });
  }

  // Only include automaticProgramming as fallback if there are no programs scheduled for today
  if (allPrograms.length === 0 && todaySchedule?.automaticProgramming) {
    const autoProgram = todaySchedule.automaticProgramming;
    allPrograms.push({
      ...autoProgram,
      id: 'auto-fallback',
      type: 'automatic',
      isLive: true,
      dj: autoProgram.dj || { name: 'AutoDJ NostalBeat', city: 'Estúdio Central' }
    });
  }

  const sortedPrograms = allPrograms.sort((a, b) => (a.start_time || '').localeCompare(b.start_time || ''));
  const liveData = liveProgram?.data;
  let activeProgram = null;

  // 1. Prioritize program matching liveProgram from API by name or ID
  if (liveData?.name) {
    const liveNameLower = liveData.name.trim().toLowerCase();
    activeProgram = sortedPrograms.find(
      (p) =>
        p.name?.trim().toLowerCase() === liveNameLower ||
        (p.id && (p.id === liveData.id || p.id === liveData._id))
    );
  }

  // 2. If not found via liveProgram, find first DJ program that is currently live by time
  if (!activeProgram) {
    activeProgram = sortedPrograms.find((p) => p.type === 'dj' && p.isLive);
  }

  // 3. Next try any program where isProgramLive(p) is true (e.g. default auto program)
  if (!activeProgram) {
    activeProgram = sortedPrograms.find((p) => p.isLive);
  }

  // 4. If nothing in sortedPrograms is marked live, but liveData exists from backend API
  if (!activeProgram && liveData && liveData.name) {
    activeProgram = {
      ...liveData,
      type: liveProgram?.type === 'program' ? 'dj' : (liveData.type || 'automatic'),
      isLive: true,
      dj: liveData.dj || { name: 'AutoDJ NostalBeat', city: 'Estúdio Central' }
    };
  }

  // 5. Fallback: if all else fails and automaticProgramming exists
  if (!activeProgram && todaySchedule?.automaticProgramming) {
    activeProgram = {
      ...todaySchedule.automaticProgramming,
      type: 'automatic',
      isLive: true,
      dj: todaySchedule.automaticProgramming.dj || { name: 'AutoDJ NostalBeat', city: 'Estúdio Central' }
    };
  }

  // Exclude activeProgram from the other programs list
  let otherPrograms = sortedPrograms;
  if (activeProgram) {
    otherPrograms = sortedPrograms.filter((p) => {
      if (activeProgram.id && p.id) {
        return p.id !== activeProgram.id;
      }
      return p.name?.trim().toLowerCase() !== activeProgram.name?.trim().toLowerCase();
    });
  }

  // Identify the immediate next program ("A seguir")
  const findNextProgram = () => {
    if (!sortedPrograms || sortedPrograms.length === 0) return null;

    if (activeProgram) {
      const activeStart = activeProgram.start_time || '';
      const activeEnd = activeProgram.end_time || '';

      // 1. Program that starts at or after activeProgram.end_time
      if (activeEnd) {
        const afterEnd = sortedPrograms.find(
          (p) =>
            p !== activeProgram &&
            p.id !== activeProgram.id &&
            p.name?.trim().toLowerCase() !== activeProgram.name?.trim().toLowerCase() &&
            p.start_time >= activeEnd
        );
        if (afterEnd) return afterEnd;
      }

      // 2. Program that starts after activeProgram.start_time
      if (activeStart) {
        const afterStart = sortedPrograms.find(
          (p) =>
            p !== activeProgram &&
            p.id !== activeProgram.id &&
            p.name?.trim().toLowerCase() !== activeProgram.name?.trim().toLowerCase() &&
            p.start_time > activeStart
        );
        if (afterStart) return afterStart;
      }
    }

    // 3. Fallback based on current São Paulo time
    const currentSPTime = new Date().toLocaleTimeString('en-GB', { timeZone: 'America/Sao_Paulo', hour12: false });
    const afterNow = sortedPrograms.find(
      (p) =>
        (!activeProgram || (p.id !== activeProgram.id && p.name?.trim().toLowerCase() !== activeProgram.name?.trim().toLowerCase())) &&
        p.start_time > currentSPTime
    );
    if (afterNow) return afterNow;

    // 4. Wrap-around to the first program of otherPrograms if current program is the last
    if (otherPrograms && otherPrograms.length > 0) {
      return otherPrograms[0];
    }

    return null;
  };

  const nextProgram = findNextProgram();

  // Verifica se um programa já terminou seu horário hoje
  const checkIsFinished = (program) => {
    if (!program || !program.end_time) return false;
    const currentSPTime = new Date().toLocaleTimeString('en-GB', { timeZone: 'America/Sao_Paulo', hour12: false });
    const start = program.start_time || '00:00';
    const end = program.end_time || '00:00';
    if (start < end) {
      return currentSPTime >= end;
    }
    // Para blocos que viram a meia-noite (ex: 22:00 às 02:00)
    return currentSPTime >= end && currentSPTime < start;
  };

  // Função para rolar automaticamente até o programa "A SEGUIR" (conforme Print 2)
  const scrollToNextProgram = (behavior = 'smooth') => {
    const container = scrollContainerRef.current;
    const target = nextProgramRef.current;
    if (!container || !target) return;

    // Calcula a posição do programa "A SEGUIR" em relação ao topo do container de rolagem
    const targetTop = target.offsetTop;
    const targetHeight = target.offsetHeight;
    const containerHeight = container.clientHeight;

    // Alinha o programa "A SEGUIR" para ficar perfeitamente visível na parte inferior (com margem de 16px)
    const desiredScrollTop = Math.max(0, targetTop - containerHeight + targetHeight + 16);

    container.scrollTo({
      top: desiredScrollTop,
      behavior: behavior,
    });
  };

  // 1. Auto-scroll ao carregar a página e quando a grade estiver pronta
  useEffect(() => {
    if (!nextProgram) return;

    const t1 = setTimeout(() => {
      scrollToNextProgram('smooth');
    }, 400);

    const t2 = setTimeout(() => {
      scrollToNextProgram('smooth');
    }, 900);

    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, [todaySchedule, nextProgram?.name, nextProgram?.id]);

  // 2. Auto-scroll reativo caso o horário mude e um novo programa se torne "A SEGUIR"
  useEffect(() => {
    const currentNextId = nextProgram?.id || nextProgram?.name;
    if (currentNextId && currentNextId !== prevNextProgramIdRef.current) {
      prevNextProgramIdRef.current = currentNextId;
      const timer = setTimeout(() => {
        scrollToNextProgram('smooth');
      }, 400);
      return () => clearTimeout(timer);
    }
  }, [nextProgram, currentTimeTick]);

  return (
    <Paper
      id="programs-section"
      elevation={8}
      sx={{
        borderRadius: 3.5,
        background: 'rgba(12, 12, 16, 0.95)',
        backdropFilter: { xs: 'none', md: 'blur(20px)' },
        border: '1.5px solid rgba(255, 107, 53, 0.28)',
        boxShadow: '0 8px 32px rgba(0, 0, 0, 0.75), 0 0 25px rgba(255, 107, 53, 0.1)',
        height: { xs: 'auto', lg: '100%' },
        maxHeight: { xs: '540px', lg: 'none' },
        flex: 1,
        minHeight: 0,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        willChange: 'auto',
        transform: 'translateZ(0)',
        transition: 'all 0.3s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <Box sx={{ px: 2, pt: 1.8, pb: 0, position: 'relative' }}>
        <SectionTitle
          title="Hoje na Rádio"
          subtitle="Acompanhe nossa programação diária"
          align="center"
          color="#FF6B35"
          sx={{ mt: 0.2, mb: 1.5, pt: 0 }}
        />
      </Box>

      {/* Live/Active Program Card (Pinned at top of schedule) */}
      {activeProgram && (
        <Box sx={{ px: 2, pt: 0.2, pb: 1, flexShrink: 0 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              p: { xs: 1.2, sm: 1.4 },
              borderRadius: 2.5,
              background: 'linear-gradient(135deg, rgba(255, 107, 53, 0.18) 0%, rgba(20, 20, 24, 0.95) 100%)',
              border: '1.5px solid #FF6B35',
              borderLeft: '4.5px solid #FF6B35',
              boxShadow: '0 4px 20px rgba(255, 107, 53, 0.28)',
              position: 'relative',
              transition: 'all 0.25s ease',
              '&:hover': {
                boxShadow: '0 6px 26px rgba(255, 107, 53, 0.42)',
              },
            }}
          >
            {/* Compact Avatar with Live Glow */}
            <Box sx={{ position: 'relative', mr: 1.5, flexShrink: 0 }}>
              <Avatar
                src={(activeProgram.type === 'dj' && !activeProgram.is_dj_absent && !activeProgram.isDjAbsent) ? fixImageUrl(activeProgram.dj?.profile_image || activeProgram.banner_image) : null}
                sx={{
                  width: { xs: 46, sm: 50 },
                  height: { xs: 46, sm: 50 },
                  border: '2px solid #FF6B35',
                  boxShadow: '0 0 12px rgba(255, 107, 53, 0.55)',
                  bgcolor: '#1a1a1a',
                  color: '#FF6B35',
                  fontSize: (activeProgram.type === 'automatic' || activeProgram.type === 'default' || activeProgram.is_dj_absent || activeProgram.isDjAbsent) ? '0.75rem' : '1rem',
                  fontWeight: 800,
                }}
              >
                {(activeProgram.type === 'automatic' || activeProgram.type === 'default' || activeProgram.is_dj_absent || activeProgram.isDjAbsent)
                  ? <Radio sx={{ fontSize: 24, color: '#FFB300' }} />
                  : (activeProgram.dj?.artisticName?.charAt(0) || activeProgram.dj?.name?.charAt(0) || 'DJ')}
              </Avatar>
              <Box
                sx={{
                  position: 'absolute',
                  bottom: -1,
                  right: -1,
                  width: 12,
                  height: 12,
                  bgcolor: '#FF6B35',
                  borderRadius: '50%',
                  border: '2px solid rgba(12, 12, 16, 0.95)',
                  animation: 'pulse 1.8s infinite',
                  '@keyframes pulse': {
                    '0%': { boxShadow: '0 0 0 0 rgba(255, 107, 53, 0.8)' },
                    '70%': { boxShadow: '0 0 0 7px rgba(255, 107, 53, 0)' },
                    '100%': { boxShadow: '0 0 0 0 rgba(255, 107, 53, 0)' },
                  },
                }}
              />
            </Box>

            {/* Program Info */}
            <Box sx={{ flex: 1, minWidth: 0, pr: { xs: 0.5, sm: 1 } }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.3 }}>
                <Typography
                  variant="subtitle2"
                  sx={{
                    color: '#FFF',
                    fontWeight: 800,
                    lineHeight: 1.2,
                    fontFamily: 'Orbitron, sans-serif',
                    fontSize: { xs: '0.85rem', sm: '0.92rem' },
                    letterSpacing: '0.3px',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {activeProgram.name}
                </Typography>

                {/* Sleek Mini NO AR Badge */}
                <Box
                  sx={{
                    bgcolor: 'rgba(0, 0, 0, 0.75)',
                    border: '1.2px solid #FF6B35',
                    borderRadius: '12px',
                    px: 1,
                    py: 0.25,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.6,
                    flexShrink: 0,
                    boxShadow: '0 0 10px rgba(255, 107, 53, 0.45)',
                    mr: { xs: 0, sm: 0.5 },
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'flex-end', height: 10, gap: '2px' }}>
                    {[0.6, 1.0, 0.7].map((delay, i) => (
                      <Box
                        key={i}
                        sx={{
                          width: 2.5,
                          bgcolor: '#FF6B35',
                          animation: 'equalizer 0.8s ease-in-out infinite alternate',
                          animationDelay: `${delay}s`,
                          '@keyframes equalizer': {
                            '0%': { height: '30%' },
                            '100%': { height: '100%' },
                          },
                        }}
                      />
                    ))}
                  </Box>
                  <Typography
                    sx={{
                      fontWeight: 900,
                      color: '#FF6B35',
                      fontSize: '0.64rem',
                      letterSpacing: '0.8px',
                      lineHeight: 1,
                      fontFamily: 'Orbitron, sans-serif',
                    }}
                  >
                    NO AR
                  </Typography>
                </Box>
              </Box>

              {/* Time & DJ / Subtitle */}
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                <Box
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 0.4,
                    px: 0.8,
                    py: 0.15,
                    borderRadius: '4px',
                    bgcolor: 'rgba(255, 107, 53, 0.18)',
                    border: '1px solid rgba(255, 107, 53, 0.35)',
                  }}
                >
                  <Schedule sx={{ fontSize: 12, color: '#FF6B35' }} />
                  <Typography
                    sx={{
                      color: '#FF8C5A',
                      fontWeight: 800,
                      fontSize: '0.72rem',
                      fontFamily: "'Roboto Mono', monospace",
                    }}
                  >
                    {displayTime(activeProgram.start_time)} - {displayTime(activeProgram.end_time)}
                  </Typography>
                </Box>
                <Typography
                  variant="caption"
                  sx={{
                    color: (activeProgram.type === 'dj' && !activeProgram.is_dj_absent && !activeProgram.isDjAbsent) ? '#FFB300' : 'rgba(255, 255, 255, 0.65)',
                    fontWeight: 700,
                    fontSize: '0.74rem',
                    whiteSpace: 'nowrap',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                  }}
                >
                  {(activeProgram.type === 'automatic' || activeProgram.type === 'default' || activeProgram.is_dj_absent || activeProgram.isDjAbsent)
                    ? ((activeProgram.is_dj_absent || activeProgram.isDjAbsent) ? 'AutoDJ NostalBeat • Playlist Especial (DJ Ausente)' : 'Programação Automática')
                    : (activeProgram.dj?.artisticName || activeProgram.dj?.name || 'DJ Convidado')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>
      )}

      {/* Program List / Scroll Container */}
      <Box
        ref={scrollContainerRef}
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden',
          scrollBehavior: 'smooth',
          px: 2,
          pb: 1.5,
          '&::-webkit-scrollbar': { width: '5px' },
          '&::-webkit-scrollbar-track': { background: 'rgba(0,0,0,0.25)', borderRadius: '4px' },
          '&::-webkit-scrollbar-thumb': {
            background: 'rgba(255, 107, 53, 0.35)',
            borderRadius: '4px',
            '&:hover': { background: '#FF6B35' },
          },
        }}
      >
        {scheduleLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress sx={{ color: '#FF6B35' }} />
          </Box>
        ) : otherPrograms.length === 0 && !activeProgram ? (
          <Box sx={{ p: 3, textAlign: 'center' }}>
            <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
              Nenhuma programação para hoje
            </Typography>
          </Box>
        ) : (
          <Box sx={{ pt: 0.5 }}>
            {otherPrograms.map((program) => {
              const isNext = Boolean(
                nextProgram &&
                ((nextProgram.id && program.id && nextProgram.id === program.id) ||
                 (nextProgram.name && program.name && nextProgram.name.trim().toLowerCase() === program.name.trim().toLowerCase()))
              );

              const isAuto = program.type === 'automatic' || program.type === 'default' || program.is_dj_absent || program.isDjAbsent;
              const isFinished = checkIsFinished(program);

              // 1. RENDER MODERNO PARA SLOTS DE AUTODJ (Alto contraste âmbar/dourado, compacto e limpo)
              if (isAuto) {
                return (
                  <Box
                    key={`${program.type}-${program.id || program._id || program.name}`}
                    ref={isNext ? nextProgramRef : null}
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: { xs: 1.2, sm: 1.6 },
                      px: { xs: 1.3, sm: 1.6 },
                      py: 0.75,
                      mb: 0.7,
                      borderRadius: '10px',
                      bgcolor: isNext
                        ? 'linear-gradient(90deg, rgba(16, 185, 129, 0.16) 0%, rgba(20, 20, 24, 0.95) 100%)'
                        : isFinished
                        ? 'rgba(255, 255, 255, 0.015)'
                        : 'linear-gradient(90deg, rgba(255, 179, 0, 0.05) 0%, rgba(255, 255, 255, 0.02) 100%)',
                      border: isNext
                        ? '1px solid rgba(16, 185, 129, 0.5)'
                        : isFinished
                        ? '1px solid rgba(255, 255, 255, 0.04)'
                        : '1px solid rgba(255, 255, 255, 0.08)',
                      borderLeft: isNext
                        ? '3.5px solid #10B981'
                        : isFinished
                        ? '3.5px solid rgba(255, 179, 0, 0.35)'
                        : '3.5px solid #FFB300',
                      opacity: isNext ? 1 : isFinished ? 0.6 : 1,
                      boxShadow: isNext ? '0 0 14px rgba(16, 185, 129, 0.2)' : 'none',
                      transition: 'all 0.25s cubic-bezier(0.4, 0, 0.2, 1)',
                      '&:hover': {
                        opacity: 1,
                        bgcolor: isNext ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 179, 0, 0.1)',
                        transform: 'translateX(3px)',
                        borderColor: isNext ? '#10B981' : 'rgba(255, 179, 0, 0.4)',
                      },
                    }}
                  >
                    {/* Badge de Horário em Destaque Âmbar / Ouro */}
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 0.4,
                        px: 1,
                        py: 0.3,
                        borderRadius: '6px',
                        bgcolor: isNext ? 'rgba(16, 185, 129, 0.16)' : isFinished ? 'rgba(255, 179, 0, 0.06)' : 'rgba(255, 179, 0, 0.12)',
                        border: isNext ? '1px solid rgba(16, 185, 129, 0.4)' : isFinished ? '1px solid rgba(255, 179, 0, 0.18)' : '1px solid rgba(255, 179, 0, 0.32)',
                        flexShrink: 0,
                      }}
                    >
                      <Typography
                        sx={{
                          fontFamily: "'Roboto Mono', monospace",
                          fontSize: '0.74rem',
                          fontWeight: 800,
                          color: isNext ? '#10B981' : isFinished ? 'rgba(255, 179, 0, 0.65)' : '#FFB300',
                          letterSpacing: '0.3px',
                          lineHeight: 1,
                        }}
                      >
                        {displayTime(program.start_time)}
                      </Typography>
                      <Typography
                        sx={{
                          fontSize: '0.7rem',
                          fontWeight: 800,
                          color: isNext ? '#34D399' : isFinished ? 'rgba(255, 160, 0, 0.5)' : '#FFA000',
                          lineHeight: 1,
                          userSelect: 'none',
                          mx: 0.1,
                        }}
                      >
                        -
                      </Typography>
                      <Typography
                        sx={{
                          fontFamily: "'Roboto Mono', monospace",
                          fontSize: '0.74rem',
                          fontWeight: 800,
                          color: isNext ? '#10B981' : isFinished ? 'rgba(255, 179, 0, 0.65)' : '#FFB300',
                          letterSpacing: '0.3px',
                          lineHeight: 1,
                        }}
                      >
                        {displayTime(program.end_time)}
                      </Typography>
                    </Box>

                    {/* Nome da Playlist em Branco Nítido */}
                    <Typography
                      sx={{
                        flex: 1,
                        minWidth: 0,
                        fontSize: '0.82rem',
                        fontWeight: 700,
                        color: isFinished ? 'rgba(255, 255, 255, 0.65)' : '#FFFFFF',
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        letterSpacing: '0.25px',
                        textShadow: '0 1px 2px rgba(0,0,0,0.6)',
                      }}
                    >
                      {program.name}
                    </Typography>

                    {/* Badge A SEGUIR ou Tag AutoDJ */}
                    {isNext ? (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4, flexShrink: 0 }}>
                        <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#10B981', boxShadow: '0 0 6px #10B981' }} />
                        <Typography sx={{ fontSize: '0.62rem', fontWeight: 900, color: '#10B981', fontFamily: 'Orbitron, sans-serif', letterSpacing: '0.5px' }}>
                          A SEGUIR
                        </Typography>
                      </Box>
                    ) : (
                      <Chip
                        label="AutoDJ"
                        size="small"
                        sx={{
                          height: 18,
                          fontSize: '0.6rem',
                          fontWeight: 800,
                          bgcolor: isFinished ? 'rgba(255, 179, 0, 0.06)' : 'rgba(255, 179, 0, 0.12)',
                          color: isFinished ? 'rgba(255, 179, 0, 0.6)' : '#FFB300',
                          border: isFinished ? '1px solid rgba(255, 179, 0, 0.15)' : '1px solid rgba(255, 179, 0, 0.25)',
                          px: 0.2,
                          flexShrink: 0,
                        }}
                      />
                    )}
                  </Box>
                );
              }

              // 2. RENDER PARA PROGRAMAS DE DJ (Com foto e destaque)
              return (
                <Box
                  key={`${program.type}-${program.id || program._id || program.name}`}
                  ref={isNext ? nextProgramRef : null}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    p: { xs: 1.1, sm: 1.3 },
                    mb: 0.8,
                    borderRadius: 2.5,
                    bgcolor: isNext
                      ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.16) 0%, rgba(20, 20, 24, 0.95) 100%)'
                      : isFinished
                      ? 'rgba(255, 255, 255, 0.02)'
                      : 'rgba(255, 255, 255, 0.03)',
                    border: isNext ? '1.5px solid rgba(16, 185, 129, 0.55)' : '1px solid rgba(255, 255, 255, 0.07)',
                    borderLeft: isNext ? '4px solid #10B981' : isFinished ? '3.5px solid rgba(255, 107, 53, 0.4)' : '3.5px solid #FF6B35',
                    opacity: isNext ? 1 : isFinished ? 0.6 : 1,
                    boxShadow: isNext ? '0 0 16px rgba(16, 185, 129, 0.22)' : 'none',
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      opacity: 1,
                      bgcolor: isNext ? 'rgba(16, 185, 129, 0.2)' : 'rgba(255, 255, 255, 0.06)',
                      transform: 'translateX(4px)',
                      borderColor: isNext ? '#10B981' : 'rgba(255, 107, 53, 0.4)',
                    },
                  }}
                >
                  <Avatar
                    src={fixImageUrl(program.dj?.profile_image || program.banner_image)}
                    sx={{
                      width: 42,
                      height: 42,
                      mr: 1.5,
                      border: isNext ? '1.5px solid #10B981' : '1.5px solid rgba(255, 107, 53, 0.4)',
                      boxShadow: isNext ? '0 0 8px rgba(16, 185, 129, 0.4)' : 'none',
                      bgcolor: 'rgba(255, 107, 53, 0.15)',
                      color: isNext ? '#10B981' : '#FF6B35',
                      fontSize: '0.85rem',
                      fontWeight: 700,
                    }}
                  >
                    {program.dj?.artisticName?.charAt(0) || program.dj?.name?.charAt(0) || 'DJ'}
                  </Avatar>

                  <Box sx={{ flex: 1, minWidth: 0, pr: { xs: 0.5, sm: 1 } }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, mb: 0.2 }}>
                      <Typography
                        variant="subtitle2"
                        sx={{
                          color: isFinished ? 'rgba(255, 255, 255, 0.7)' : '#FFFFFF',
                          fontWeight: 700,
                          lineHeight: 1.2,
                          fontFamily: 'Orbitron, sans-serif',
                          fontSize: '0.82rem',
                          whiteSpace: 'nowrap',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                        }}
                      >
                        {program.name}
                      </Typography>

                      {/* Badge A seguir */}
                      {isNext && (
                        <Box
                          sx={{
                            bgcolor: 'rgba(0, 0, 0, 0.75)',
                            border: '1.2px solid #10B981',
                            borderRadius: '12px',
                            px: 0.9,
                            py: 0.2,
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5,
                            flexShrink: 0,
                            boxShadow: '0 0 10px rgba(16, 185, 129, 0.4)',
                          }}
                        >
                          <Box sx={{ width: 5, height: 5, borderRadius: '50%', bgcolor: '#10B981', boxShadow: '0 0 6px #10B981' }} />
                          <Typography
                            sx={{
                              fontWeight: 900,
                              color: '#10B981',
                              fontSize: '0.62rem',
                              letterSpacing: '0.6px',
                              lineHeight: 1,
                              fontFamily: 'Orbitron, sans-serif',
                            }}
                          >
                            A SEGUIR
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, flexWrap: 'wrap' }}>
                      <Typography
                        sx={{
                          color: isNext ? '#10B981' : isFinished ? 'rgba(255, 140, 90, 0.65)' : '#FF8C5A',
                          fontWeight: 700,
                          fontSize: '0.72rem',
                          fontFamily: "'Roboto Mono', monospace",
                        }}
                      >
                        {displayTime(program.start_time)} - {displayTime(program.end_time)}
                      </Typography>
                      <Box sx={{ width: 3, height: 3, borderRadius: '50%', bgcolor: 'rgba(255, 255, 255, 0.3)' }} />
                      <Typography
                        variant="caption"
                        sx={{
                          color: isFinished ? 'rgba(255, 255, 255, 0.45)' : 'rgba(255, 255, 255, 0.65)',
                          fontWeight: 600,
                          fontSize: '0.72rem',
                        }}
                      >
                        {program.dj?.artisticName || program.dj?.name || 'DJ Convidado'}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              );
            })}

            {otherPrograms.length === 0 && !activeProgram && (
              <Typography align="center" variant="caption" sx={{ display: 'block', mt: 2, color: 'rgba(255,255,255,0.4)' }}>
                Sem mais programas para hoje
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Paper>
  );
};

export default TodayScheduleSection;
